import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccountMembership, APIService, CreateCustomFormAttachConditionInput, CreateCustomFormDefinitionsInput, CreateCustomPropertyDefinitionsInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AiCallOutcome, AiContactProperty } from '../ai-workspace-setup.models';
import { stringToUppercaseUnderscore } from '../../../../../globals/functions';

interface FormFieldEntry {
  name: string;
  fieldName: string;
  type: 'Text' | 'Number' | 'Option' | 'DateTime';
  options?: string[];
  isMandatory: boolean;
  included: boolean;
  isNew: boolean; // true = needs to be created as a custom property first
}

@Component({
  selector: 'ws-qualification-form',
  templateUrl: './ws-qualification-form.component.html',
  imports: [FormsModule, NgClass, MatButton, DragDropModule]
})
export class WsQualificationFormComponent implements OnInit, OnChanges {
  @Input() callOutcomes: AiCallOutcome[];
  @Input() customProperties: AiContactProperty[];
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<void>();
  @Output() feedbackRequested = new EventEmitter<string>();

  fields: FormFieldEntry[] = [];
  isSaving = false;
  existingForm: any = null;
  feedback = '';

  newFieldName = '';
  newFieldType: 'Text' | 'Number' | 'Option' | 'DateTime' = 'Text';

  private readonly SYSTEM_PROPERTY_NAMES = [
    'LEADSOURCE', 'LEAD_SOURCE', 'CONTACTSOURCE', 'CONTACT_SOURCE',
    'LEADLIFECYCLENAME', 'LEAD_LIFECYCLE_NAME',
    'DEALSTAGE', 'DEAL_STAGE', 'LEADTAG', 'LEAD_TAG',
    'CALLOUTCOME', 'CALL_OUTCOME', 'TASKTYPE', 'TASK_TYPE',
    'VISITOUTCOME', 'VISIT_OUTCOME', 'PROSPECTINGSCHEDULE', 'PROSPECTING_SCHEDULE',
    'NAME', 'DEPARTMENT', 'DESIGNATION', 'PRODUCTSERVICE', 'PRODUCT_SERVICE',
  ];

  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.initFields();
    if (!this.testMode) this.checkExistingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customProperties'] && this.customProperties) {
      this.initFields();
    }
  }

  private initFields() {
    // Build form fields from AI-suggested custom properties
    this.fields = (this.customProperties || [])
      .filter(p => p.selected !== false)
      .map(p => ({
        name: p.name,
        fieldName: p.fieldName || stringToUppercaseUnderscore(p.name),
        type: p.type,
        options: p.options,
        isMandatory: false,
        included: true,
        isNew: false,
      }));
  }

  private async checkExistingData() {
    if (!this.activeAccount?.accountId) return;
    try {
      const result = await this.api.ListCustomFormDefinitionss(
        { accountId: { eq: this.activeAccount.accountId } } as any, 100
      );
      if (result?.items?.length) {
        this.existingForm = result.items.find((f: any) =>
          f.formName === 'QUALIFICATION_FORM' || f.displayName === 'Qualification Form'
        );
      }
    } catch (e) {
      console.error('Error checking existing forms:', e);
    }
  }

  toggleField(field: FormFieldEntry) {
    field.included = !field.included;
  }

  toggleMandatory(field: FormFieldEntry) {
    field.isMandatory = !field.isMandatory;
  }

  removeField(field: FormFieldEntry) {
    this.fields = this.fields.filter(f => f !== field);
  }

  reorderFields(event: CdkDragDrop<FormFieldEntry[]>) {
    moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
  }

  addField() {
    if (!this.newFieldName.trim()) return;
    const fieldName = stringToUppercaseUnderscore(this.newFieldName.trim());
    if (this.SYSTEM_PROPERTY_NAMES.includes(fieldName)) {
      this.toastr.warning(`"${this.newFieldName.trim()}" conflicts with a built-in system property`);
      return;
    }
    this.fields.push({
      name: this.newFieldName.trim(),
      fieldName,
      type: this.newFieldType,
      isMandatory: false,
      included: true,
      isNew: true,
    });
    this.newFieldName = '';
  }

  submitFeedback() {
    if (this.feedback.trim()) {
      this.feedbackRequested.emit(this.feedback.trim());
      this.feedback = '';
    }
  }

  async save() {
    if (this.isSaving) return;
    const included = this.fields.filter(f => f.included);
    if (included.length === 0) {
      this.toastr.warning('Please include at least one field in the form');
      return;
    }

    if (this.testMode) {
      this.toastr.success('Test mode: Qualification form saved');
      this.completed.emit();
      return;
    }

    if (this.existingForm) {
      this.toastr.info('Qualification form already exists');
      this.completed.emit();
      return;
    }
    this.isSaving = true;

    try {
      // Create ALL fields as custom properties and capture both the id AND the
      // backend-assigned propertyName. The backend Lambda
      // (createCustomPropertyDefinitions) overwrites the client-sent
      // propertyName for non-system properties with an auto-generated name like
      // text_contact_N / number_contact_N. The form schema's fieldName must
      // match that actual propertyName, otherwise downstream lookups by key
      // (saved-data display, option viewValue resolution) silently degrade.
      const propMap: Record<string, { id: string; propertyName: string }> = {};
      for (const field of included) {
        const possibleValues = field.type === 'Option' && field.options?.length
          ? field.options.map(opt => ({ value: stringToUppercaseUnderscore(opt), viewValue: opt }))
          : undefined;

        const propInput = <CreateCustomPropertyDefinitionsInput>{
          accountId: this.activeAccount.accountId,
          propertyName: field.fieldName,
          displayName: field.name,
          description: field.name,
          scalarType: field.type,
          isSystemProperty: false,
          parentObjectType: 'Contact',
          possibleValues: possibleValues || [],
          createdById: this.activeAccount.userId,
          updaedById: this.activeAccount.userId,
        };
        const created = await this.api.CreateCustomPropertyDefinitions(propInput);
        if (created?.id) {
          propMap[field.fieldName] = {
            id: created.id,
            propertyName: created.propertyName || field.fieldName,
          };
        }
      }

      // Build form schema from included fields
      const viewTypeMap: Record<string, string> = {
        'Text': 'StringType',
        'Number': 'NumberType',
        'Option': 'EnumType',
        'DateTime': 'DateType',
      };

      const formFields = included.map(f => ({
        fieldName: propMap[f.fieldName]?.propertyName ?? f.fieldName,
        fieldParentObjectType: 'Contact',
        propertyDefinitionId: propMap[f.fieldName]?.id ?? null,
        viewType: viewTypeMap[f.type] || 'StringType',
        isMandatory: f.isMandatory,
        isViewOnly: false,
        isHidden: false,
        hintText: null,
        preSetValue: null,
        showCondition: null,
        hideCondition: null,
      }));

      const input = <CreateCustomFormDefinitionsInput>{
        accountId: this.activeAccount.accountId,
        formName: 'QUALIFICATION_FORM',
        displayName: 'Qualification Form',
        formTitle: 'Qualification Form',
        formSchema: [{ stepTitle: 'step-1', fields: formFields, fieldGroups: [] }],
        createdById: this.activeAccount.userId,
        updaedById: this.activeAccount.userId,
      };

      const form = await this.api.CreateCustomFormDefinitions(input);

      // Link form to "Interested" call outcome
      const interestedOutcome = (this.callOutcomes || []).find(
        o => o.nature === 'positive' && (o.startsNewDeal || o.name.toLowerCase().includes('interested'))
      );

      if (interestedOutcome && form) {
        const linkInput = <CreateCustomFormAttachConditionInput>{
          accountId: this.activeAccount.accountId,
          formId: (form as any).id,
          formType: 'callOutcomeForm',
          callOutcome: interestedOutcome.value || stringToUppercaseUnderscore(interestedOutcome.name),
          createdById: this.activeAccount.userId,
          updatedById: this.activeAccount.userId,
        };
        await this.api.CreateCustomFormAttachCondition(linkInput);
        this.toastr.success('Qualification form created & linked to "' + interestedOutcome.name + '"');
      } else {
        this.toastr.success('Qualification form created');
      }

      this.completed.emit();
    } catch (e: any) {
      console.error('Error creating form:', e);
      this.toastr.error(e?.errors?.[0]?.message || 'Failed to create form');
    } finally {
      this.isSaving = false;
    }
  }
}
